<script setup>
import { ref, watch, computed } from 'vue';
import { formataTamanhoArquivo, openPDF } from '@/commons/utils';
import { FILE_CONSTRAINTS } from '@/constants/solicitation';

const props = defineProps({
    modelValue: {
        type: File,
        default: null
    },
    error: {
        type: String,
        default: null
    },
    label: {
        type: String,
        default: 'Proposta de Referência'
    },
    required: {
        type: Boolean,
        default: true
    },
    accept: {
        type: String,
        default: '.pdf'
    },
    acceptDescription: {
        type: String,
        default: null // Se null, será gerado automaticamente
    },
    maxSizeMB: {
        type: Number,
        default: FILE_CONSTRAINTS.maxSizeMB
    }
});

// Mapeamento de tipos de arquivo para ícones e descrições
const fileTypeConfig = {
    '.pdf': {
        icon: '/images/files/pdf.svg',
        description: 'PDF',
        mimeType: 'application/pdf'
    },
    '.xlsx': {
        icon: '/images/files/xls.svg',
        description: 'Excel (XLSX)',
        mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    },
    '.xls': {
        icon: '/images/files/xls.svg',
        description: 'Excel (XLS)',
        mimeType: 'application/vnd.ms-excel'
    },
    '.doc': {
        icon: '/images/files/word.svg',
        description: 'Word (DOC)',
        mimeType: 'application/msword'
    },
    '.docx': {
        icon: '/images/files/word.svg',
        description: 'Word (DOCX)',
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    },
    '.png': {
        icon: '/images/files/image.svg',
        description: 'Imagem PNG',
        mimeType: 'image/png'
    },
    '.jpg,.jpeg': {
        icon: '/images/files/image.svg',
        description: 'Imagem JPG/JPEG',
        mimeType: 'image/jpeg'
    },
    '.zip': {
        icon: '/images/files/zip.svg',
        description: 'Arquivo ZIP',
        mimeType: 'application/zip'
    }
};

// Computar configuração do tipo de arquivo
const currentFileType = computed(() => {
    const acceptTypes = props.accept.split(',').map(t => t.trim().toLowerCase());

    // Procurar configuração exata
    for (const type of acceptTypes) {
        if (fileTypeConfig[type]) {
            return fileTypeConfig[type];
        }
    }

    // Configuração padrão para tipos não mapeados
    return {
        icon: '/images/files/file.svg',
        description: acceptTypes.join(', ').toUpperCase(),
        mimeType: null
    };
});

// Descrição dos tipos aceitos para exibir na mensagem
const acceptedTypesDescription = computed(() => {
    if (props.acceptDescription) {
        return props.acceptDescription;
    }

    const acceptTypes = props.accept.split(',').map(t => t.trim());
    const descriptions = acceptTypes.map(type => {
        const config = fileTypeConfig[type];
        return config ? config.description : type.replace('.', '').toUpperCase();
    });

    return descriptions.join(', ');
});

const emit = defineEmits(['update:modelValue', 'change']);

const fileUploader = ref(null);
const selectedFile = ref(null);

// Observar mudanças no modelValue para sincronizar (útil na edição)
watch(() => props.modelValue, (newValue) => {
    if (newValue && newValue !== selectedFile.value) {
        selectedFile.value = newValue;

        // Atualizar o componente FileUpload se existir
        if (fileUploader.value && newValue instanceof File) {
            fileUploader.value.files = [newValue];
        }
    } else if (newValue === null && selectedFile.value !== null) {
        selectedFile.value = null;
        if (fileUploader.value) {
            fileUploader.value.files = [];
        }
    }
});

const handleFileSelect = async (event) => {
    const file = event.files[0];
    selectedFile.value = file;
    emit('update:modelValue', file);
    emit('change', file);
};

const handleFileRemove = (index, removeFileCallback) => {
    removeFileCallback(index);
    selectedFile.value = null;
    emit('update:modelValue', null);
    emit('change', null);
};

const handleOpenPDF = (file) => {
    openPDF(file);
};

// Obter ícone baseado no tipo de arquivo
const getFileIcon = (file) => {
    // Verificar por MIME type
    for (const config of Object.values(fileTypeConfig)) {
        if (config.mimeType === file.type) {
            return config.icon;
        }
    }

    // Verificar por extensão do nome do arquivo
    const fileName = file.name.toLowerCase();
    for (const [ext, config] of Object.entries(fileTypeConfig)) {
        const extensions = ext.split(',').map(e => e.trim());
        if (extensions.some(e => fileName.endsWith(e))) {
            return config.icon;
        }
    }

    // Ícone padrão se não encontrar
    return currentFileType.value.icon;
};
</script>

<template>
    <div class="field mb-2 col-12">
        <SmallTitle :titulo="label" :obrigatorio="required" />

        <FileUpload ref="fileUploader" :multiple="false" :fileLimit="1" :accept="accept" chooseLabel="Escolha o arquivo"
            :showUploadButton="false" @select="handleFileSelect" :pt="{
                root: {
                    class: error ? 'invalid-feedback flex flex-column-reverse' : 'flex flex-column-reverse'
                },
                content: {
                    class: 'p-3',
                    style: 'border-top-right-radius: 6px; border-top-left-radius: 6px; border-bottom-right-radius: 0px; border-bottom-left-radius: 0px; '
                },
                buttonbar: {
                    class: 'py-2 px-3',
                    style: 'border-radius: 0px 0px 6px 6px; border: solid 1px #e5e7eb; border-top: 0px;'
                }

            }">

            <template #header="{ files, chooseCallback }">
                <div class="w-full flex gap-3 flex-row justify-content-between align-items-center">

                    <div
                        class="w-full flex flex-column md:flex-row justify-content-between align-items-start md:align-items-center py-3">
                        <p class="text-xs m-0 text-500">Apenas arquivos <span
                                class="font-semibold">{{ acceptedTypesDescription }}</span>.</p>
                        <p class="text-xs m-0 text-500">Tamanho Máx: <span
                                class="font-semibold">{{ maxSizeMB }}mb</span>.</p>
                    </div>

                    <Button v-if="files.length === 0" @click="chooseCallback()" icon="pi pi-upload text-xs" outlined
                        severity="secondary" style="padding: 0.5rem 0.7rem" class="border-circle" size="small"
                        v-tooltip.bottom="{
                            value: 'Escolha o arquivo',
                        }" />
                </div>
            </template>

            <template #content="{ files, removeFileCallback }">
                <div class="flex mt-0 flex-wrap gap-3" v-if="files.length > 0">
                    <div v-for="(file, index) of files" :key="file.name + file.type + file.size"
                        class="border-1 surface-border z-1 p-3 border-round-2xl cursor-pointer flex flex-row gap-3 hover:surface-100 p-ripple">
                        <div @click="handleOpenPDF(file)" class="flex flex-column md:flex-row gap-3">
                            <img :src="getFileIcon(file)" class="w-1rem md:w-2rem" alt="Ícone do arquivo" />
                            <div class="flex flex-column justify-content-center">
                                <span class="text-900 text-sm font-medium">{{ file.name }}</span>
                                <span class="text-400 text-xs">{{ formataTamanhoArquivo(file.size) }}</span>
                            </div>
                        </div>

                        <Button icon="pi pi-trash"
                            class="z-5 relative p-button-danger border-transparent p-button-outlined p-button-sm"
                            outlined title="Remover anexo" @click="handleFileRemove(index, removeFileCallback)" />
                    </div>
                </div>
            </template>

            <template #empty>
                <div>
                    <small>Nenhum arquivo selecionado</small>
                </div>
            </template>
        </FileUpload>

        <SmallError :error="error" />
    </div>
</template>
